import declare from "dojo/_base/declare";
import Widget from "csi/table/Widget";
import reflection from "csi/reflection";
import xml from "csi/xml";
import Nodes, { StoreNodes } from "csi/store/Nodes";
import _ from "lodash";
import locale from "csi/locale";
import { QueryLevel } from "csi/view/_query/Level";
import { Context } from "csi/def/Context";
import BooleanController from "csi/heading/BooleanController";
import { C8Node } from "csi/typings";

let lockedAttributes: Record<string, string>;

const _LockController = declare(BooleanController, {

	/**
	 * @override
	 */
	process(): void {
		const nodeId = this.getNode().Id;
		const nodeParent = this.getNode().__Parent__;
		if (this._getCheckboxValue()) {
			lockedAttributes[nodeId] = nodeParent;
		} else {
			delete lockedAttributes[nodeId];
		}
		const setupSettingsUrl = this.getContext().defView.getSourceNode().SetupSettings;
		this.m_writer.changeNode(setupSettingsUrl).changeAttribute("LockedAttributes", xml.ATTR_STRINGMAP, lockedAttributes);
		this.inherited(arguments);
	},
});

const nodeTypes = ["PartMaterial", "PartMaterialColor"];

const _getStore = (ctx: Context, bConfigurable: any): StoreNodes => {
	const dataNodes = [];
	const ctxNode = ctx.getNode();
	if (ctxNode._data) {
		const children = getAttributeStore(ctx, ctxNode.$Type);
		dataNodes.push(...children);
	} else {
		// Create a root node for the query
		nodeTypes.forEach((nodeType) => {
			const root = ctx.cache.createClientNode(null, nodeType, locale.type(nodeType));
			root._data = {
				level: 0,
			};
			dataNodes.push(root);
		});
	}
	const results = new Nodes({
		bSimpleQueryEngine: true,
		cachingStore: ctx.cache,
		data: dataNodes,
	});
	return results;
};

/**
 * Create nodes based on reflection attributes and sort by name
 * @param ctx
 * @param nodeType 
 * @returns List of Nodes created from reflection attributes
 */
const getAttributeStore = (ctx: Context, nodeType: string): C8Node[] => {
	const bomSubtypeSetupSettingsUrl = ctx.defView.getSource().getNode().SetupSettings;
	lockedAttributes = ctx.cache.get(bomSubtypeSetupSettingsUrl).LockedAttributes;
	const rc = reflection.getClass(nodeType);
	let children = rc.getAttributes(true, true).filter((rcr) => !rcr.isReadOnly && !rcr.isHidden)
		.map((rcr) => {
			const id = rcr.getId();
			const name = locale.typeId(nodeType, id);
			const child = ctx.cache.createClientNode(null, "ConfigurableAttribute", name);
			child._data = {
				level: 1,
			};
			const isLocked = lockedAttributes[id] === nodeType;
			xml.nodeCreateClientAttr(child, "Lock", xml.ATTR_BOOLEAN, isLocked);
			xml.nodeCreateClientAttr(child, "Id", xml.ATTR_STRING, id);
			xml.nodeCreateClientAttr(child, xml.ATTR_PARENT, xml.ATTR_REF, nodeType);
			return child;
		});
	children = xml.sortNodes(children);

	return children;
};

const LockHostAttributesController = declare(Widget, {

	disableBatchFilter: true,
	disableCustomViewToolbar: true,
	plugins: [],
	pluginsRight: [],
	nodeTypes: ["PartMaterial", "PartMaterialColor"],
	childNodeType: "ConfigurableAttribute",

	expandLevel: 2,

	_actionControllers: {
		EditLock: _LockController,
	},

	/**
	 * @override
	 */
	_resolveView() {
		this.relRoot.m_typeNode = this.childNodeType;
		this.inherited(arguments);
	},

	/**
	 * Use custom store for the main query level
	 * @param ql
	 */
	onCreateMainQueryLevel(ql: QueryLevel): void {
		ql.createCustomStore = _getStore;
		ql.hasChildrenTest = function (ctx): boolean {
			return !ctx.getNode().__Parent__;
		};
	},
});

export = LockHostAttributesController;